import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { isNil } from 'lodash';
import {
  Box,
  Paper,
  Drawer,
  useTheme as useThemeV5,
  ThemeProvider,
  createTheme
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import { TagsContainer } from "../TagsContainer";

import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const themeV4 = useThemeV4();

  // Theme Bridge
  const theme = useMemo(() => createTheme({
    palette: {
      mode: themeV4.palette.type,
      primary: themeV4.palette.primary,
      secondary: themeV4.palette.secondary,
      error: themeV4.palette.error,
      warning: themeV4.palette.warning,
      info: themeV4.palette.info,
      success: themeV4.palette.success,
      text: themeV4.palette.text,
      background: themeV4.palette.background,
      divider: themeV4.palette.divider,
    },
    typography: {
      fontFamily: themeV4.typography.fontFamily,
    }
  }), [themeV4]);

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const { companyId } = user;

  // Fetch Ticket Data
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          if (!isNil(ticketId) && ticketId !== "undefined") {
            const { data } = await api.get("/tickets/u/" + ticketId);
            setContact(data.contact);
            setTicket(data);
            if (["pending", "open", "group"].includes(data.status)) {
              setTabOpen(data.status);
            }
            setLoading(false);
          }
        } catch (err) {
          history.push("/tickets");
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history, setTabOpen]);

  // Socket Logic
  useEffect(() => {
    if (!ticket?.id) return;

    if (user.companyId) {
      const onConnectTicket = () => socket.emit("joinChatBox", `${ticket.id}`);

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket.id === ticket.id) {
          setTicket(data.ticket);
        }
        if (data.action === "delete" && data.ticketId === ticket.id) {
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
        }
      };

      socket.on("connect", onConnectTicket);
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {
        socket.emit("joinChatBoxLeave", `${ticket.id}`);
        socket.off("connect", onConnectTicket);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
      };
    }
  }, [ticketId, ticket, history, user.companyId, socket]);

  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  const renderMessagesList = () => (
    <>
      <MessagesList
        isGroup={ticket.isGroup}
        onDrop={setDragDropFiles}
        whatsappId={ticket.whatsappId}
        queueId={ticket.queueId}
        channel={ticket.channel}
      />
      <MessageInput
        ticketId={ticket.id}
        ticketStatus={ticket.status}
        ticketChannel={ticket.channel}
        droppedFiles={dragDropFiles}
        contactId={contact.id}
      />
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        display: "flex",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        gap: 2 // Gap between Chat and Drawer
      }}>
        <Paper
          variant="outlined"
          elevation={0}
          sx={{
            flex: 1, // Chat takes all available space
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 4,
            boxShadow: 1,
            position: 'relative'
          }}
        >
          <TicketHeader loading={loading}>
            {ticket.contact !== undefined && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TicketInfo
                  contact={contact}
                  ticket={ticket}
                  onClick={handleDrawerOpen}
                />
              </Box>
            )}
            <TicketActionButtons
              ticket={ticket}
              contact={contact}
            />
          </TicketHeader>

          {/* <Paper square elevation={0} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <TagsContainer contact={contact} />
          </Paper> */}

          <ReplyMessageProvider>
            <ForwardMessageProvider>
              <EditMessageProvider>
                {renderMessagesList()}
              </EditMessageProvider>
            </ForwardMessageProvider>
          </ReplyMessageProvider>
        </Paper>

        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          contact={contact}
          loading={loading}
          ticket={ticket}
        />
      </Box>
    </ThemeProvider>
  );
};

export default Ticket;
